import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog';
import { Maximize2 } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface FullScreenImageDialogProps {
    children?: React.ReactNode;
    imageUrl?: string; // If provided, shows this image in full screen.
    dialogContent?: React.ReactNode;
    triggerClassName?: string;
    altText?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    closeOnImageClick?: boolean;
    /** Edge-to-edge dialog (100dvh) for self-assessment mobile. */
    immersive?: boolean;
}

export function FullScreenImageDialog({
    children,
    imageUrl,
    dialogContent,
    triggerClassName = 'relative group cursor-pointer w-full h-full flex items-center justify-center',
    altText = 'Full screen view',
    open,
    onOpenChange,
    closeOnImageClick = true,
    immersive = false,
}: FullScreenImageDialogProps) {
    const [internalOpen, setInternalOpen] = useState(false);

    const isControlled = open !== undefined;
    const dialogOpen = isControlled ? open : internalOpen;

    const handleOpenChange = (newOpen: boolean) => {
        if (!isControlled) {
            setInternalOpen(newOpen);
        }
        if (onOpenChange) {
            onOpenChange(newOpen);
        }
    };

    // If no imageUrl is provided, we try to extract it from children if it's an img, 
    // but mostly this component expects the caller to provide the source or the children to handle it.
    // For canvas, caller should render the image themselves.
    return (
        <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
            {children && (
                <DialogTrigger asChild>
                    <div className={triggerClassName}>
                        {children}
                        <div className="absolute inset-0 bg-fg/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-md pointer-events-none">
                            <Maximize2 className="text-bg w-8 h-8 drop-shadow-md" />
                        </div>
                    </div>
                </DialogTrigger>
            )}

            <DialogContent
                hideCloseButton={Boolean(dialogContent)}
                className={immersive
                ? 'max-w-none w-screen max-h-[100dvh] h-[100dvh] rounded-none p-0 bg-bg border-none flex flex-col items-center justify-center z-[60]'
                : 'max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-1 bg-bg border-none flex flex-col items-center justify-center'}>
                <VisuallyHidden>
                    <DialogTitle>{altText}</DialogTitle>
                </VisuallyHidden>
                {dialogContent ? (
                    dialogContent
                ) : imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={altText}
                        className="w-full h-full object-contain select-none cursor-pointer"
                        onClick={() => {
                            if (closeOnImageClick) {
                                handleOpenChange(false);
                            }
                        }}
                    />
                ) : (
                    <div className="text-fg p-4">Image source not available</div>
                )}
            </DialogContent>
        </Dialog>
    );
}
